import { supabase } from "@/integrations/supabase/client";
import { safeLocalStorage } from "@/lib/safeStorage";

export type UserRole = "admin" | "producer" | "crew";

export interface UserSession {
  userId: string;
  email: string;
  roles: UserRole[];
  hasActiveSubscription: boolean;
  trialEndsAt: string | null;
  manualAccess: boolean;
}

const SESSION_CACHE_KEY = "produce_cached_user_session";

// Get cached session for offline use
function getOfflineSession(): UserSession | null {
  try {
    const cached = safeLocalStorage.getItem(SESSION_CACHE_KEY);
    if (!cached) return null;

    const { session, timestamp } = JSON.parse(cached);
    // Check if cache is within 7 days
    if (timestamp && Date.now() - timestamp < 7 * 24 * 60 * 60 * 1000) {
      return session;
    }
    return null;
  } catch (e) {
    console.error(e);
    return null;
  }
}

// Cache session for offline use
function cacheSession(session: UserSession): void {
  try {
    safeLocalStorage.setItem(
      SESSION_CACHE_KEY,
      JSON.stringify({
        session,
        timestamp: Date.now(),
      }),
    );
  } catch (e) {
    console.error(e);
  }
}

export async function getCurrentUserSession(): Promise<UserSession | null> {
  try {
    // If offline, try cached session first
    if (!navigator.onLine) {
      return getOfflineSession();
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return null;

    // Fetch all roles for user
    const { data: rolesData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id);

    const roles = rolesData?.map((r) => r.role as UserRole) || [];

    // Fetch subscription if user has producer role
    let hasActiveSubscription = false;
    let trialEndsAt: string | null = null;
    let manualAccess = false;

    if (roles.includes("producer")) {
      const { data: subData } = await supabase
        .from("producer_subscriptions")
        .select("plan_status, trial_ends_at")
        .eq("user_id", session.user.id)
        .single();

      const { data: profileData } = await supabase
        .from("profiles")
        .select("manual_producer_access")
        .eq("id", session.user.id)
        .single();

      // Check if trial is active - also consider 'expired' status with active trial_ends_at
      // This handles edge cases where status is inconsistent with trial date
      const isTrialActive =
        (subData?.plan_status === "trial" ||
          (subData?.plan_status === "expired" && subData?.trial_ends_at)) &&
        subData?.trial_ends_at &&
        new Date(subData.trial_ends_at) > new Date();

      hasActiveSubscription = subData?.plan_status === "active" || !!isTrialActive;
      trialEndsAt = subData?.trial_ends_at || null;
      manualAccess = profileData?.manual_producer_access || false;
    }

    const userSession: UserSession = {
      userId: session.user.id,
      email: session.user.email!,
      roles,
      hasActiveSubscription,
      trialEndsAt,
      manualAccess,
    };

    // Cache session for offline use
    cacheSession(userSession);

    return userSession;
  } catch {
    return getOfflineSession();
  }
}

export function canAccessProducerPortal(session: UserSession | null): boolean {
  if (!session) return false;
  if (session.roles.includes("admin")) return true;
  if (!session.roles.includes("producer")) return false;
  return session.hasActiveSubscription || session.manualAccess;
}

export function isAdmin(session: UserSession | null): boolean {
  return session?.roles.includes("admin") || false;
}
